import { css, cx } from '@fable/theme'
import { FlowerHead, FlowerHeadPath } from '../FlowerHead'
import { FlowerPot } from '../FlowerPot'
import { FlowerLeaves } from '../FlowerLeaves'
import { useRef, useState } from 'react'
import { FlowerStalk } from '../FlowerStalk'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import { getFlowerColor } from '../utils'
import { FlowerProps } from 'app/webview/pages/stats_profile/lib/types'

export const FlowerHappy = ({
  id,
  head,
  face,
  progress,
  total,
  motion,
  startPetalAnim,
  onPetalsFilled,
}: FlowerProps) => {
  const container = useRef<HTMLDivElement>(null)
  const flowerColors = getFlowerColor(head)
  const [isViewed, setIsViewed] = useState(false)

  useGSAP(
    () => {
      if (!startPetalAnim || !isViewed || total === 0) return

      const tl = gsap.timeline({ onComplete: onPetalsFilled })
      const petals = gsap.utils.toArray('.anim__flower-petal')
      const totalPetals = petals.length
      const result = Math.floor((progress / total) * totalPetals)
      const petalNum = result > totalPetals ? totalPetals : result
      const petalsToFill = petals.slice(0, petalNum) as HTMLElement[]

      tl.to(petalsToFill, {
        duration: 0.1,
        fill: flowerColors.filled,
        stagger: 0.15,
        ease: 'sine.inOut',
      })

      const overcomplete = progress > total

      if (overcomplete) {
        const remainingPetals = result - totalPetals
        const moveDistance = 10
        const maxLoopCount = 3
        const maxAnimations = totalPetals * maxLoopCount
        const cappedPetalCount = Math.min(remainingPetals, maxAnimations)

        for (let i = 0; i < cappedPetalCount; i++) {
          const index = i % totalPetals
          const petal = petals[index] as HTMLElement

          const angle = (index / totalPetals) * Math.PI * 2 - Math.PI / 2
          const xOffset = Math.round(moveDistance * Math.cos(angle))
          const yOffset = Math.round(moveDistance * Math.sin(angle))

          tl.to(petal, {
            duration: 0.075,
            x: `+=${xOffset}`,
            y: `+=${yOffset}`,
            repeat: 1,
            yoyo: true,
            ease: 'bounce.inOut',
          })

          tl.to(
            petal,
            {
              duration: 0.1,
              fill: flowerColors.filled,
              ease: 'sine.inOut',
            },
            '<'
          )
        }
      }
    },
    { scope: container, dependencies: [startPetalAnim, isViewed] }
  )

  const appear = () => {
    const tl = gsap.timeline()

    tl.to('.anim__flower', {
      opacity: 1,
      scale: 1,
      duration: 0.3,
      ease: 'power2.out',
    })

    return tl
  }

  const moveHead = () => {
    const tl = gsap.timeline({ repeat: 5, yoyo: true })

    const motionPath = `#flowerHeadPath-${id}`

    tl.to('.anim__flower-head', {
      top: '50%',
      left: '50%',
      xPercent: -50,
      yPercent: -50,
      transformOrigin: '50% 50%',
      duration: 1.5,
      ease: 'sine.inOut',
      motionPath: {
        path: motionPath,
        align: motionPath,
        start: 0.25,
        end: 0.75,
      },
    })

    return tl
  }

  const moveStalk = () => {
    const tl = gsap.timeline({ repeat: 2, yoyo: true })

    const left = 'm13.24,51.39C13.24,34.76,1.5,3.79,1.5,3.79'
    const center = 'm13.24,1.5v49.89'
    const right = 'm13.24,51.39c0-16.63,11.74-47.6,11.74-47.6'

    tl.to('.anim__flower-stalk path', {
      duration: 0.75,
      ease: 'sine.in',
      morphSVG: center,
    })
    tl.to('.anim__flower-stalk path', {
      duration: 0.75,
      ease: 'sine.out',
      morphSVG: right,
    })
    tl.to('.anim__flower-stalk path', {
      duration: 0.75,
      ease: 'sine.in',
      morphSVG: center,
    })
    tl.to('.anim__flower-stalk path', {
      duration: 0.75,
      ease: 'sine.out',
      morphSVG: left,
    })

    return tl
  }

  const moveLeaves = () => {
    const tl = gsap.timeline({ repeat: 5, yoyo: true, delay: 0.5 })

    tl.to('.anim__flower-leaves', {
      rotate: 5,
      delay: 0,
      duration: 1.5,
      ease: 'sine.inOut',
    })

    return tl
  }

  const finalPos = () => {
    const tl = gsap.timeline()

    const motionPath = `#flowerHeadPath-${id}`

    tl.to('.anim__flower-head', {
      top: '50%',
      left: '50%',
      xPercent: -50,
      yPercent: -50,
      transformOrigin: '50% 50%',
      duration: 0.75,
      ease: 'sine.inOut',
      motionPath: {
        path: motionPath,
        align: motionPath,
        start: 0.25,
        end: 0.5,
      },
    })

    tl.to(
      '.anim__flower-stalk path',
      {
        duration: 0.75,
        ease: 'sine.inOut',
        morphSVG: 'm13.24,1.5v49.89',
      },
      '<'
    )

    tl.to(
      '.anim__flower-leaves',
      {
        rotate: 0.25,
        duration: 0.75,
        ease: 'sine.inOut',
      },
      '<'
    )

    return tl
  }

  useGSAP(
    () => {
      const tl = gsap.timeline({
        delay: motion?.delay || 0,
        scrollTrigger: {
          trigger: container.current,
          start: '150% bottom',
          once: true,
          onToggle: (self) => !isViewed && self.isActive && setIsViewed(true),
        },
      })

      tl.add(appear(), 'start')
      tl.add(moveHead(), '<start')
      tl.add(moveStalk(), '<start')
      tl.add(moveLeaves(), '<start')
      tl.add(finalPos(), '-=0.5')
    },
    { scope: container }
  )

  return (
    <div
      ref={container}
      className={css`
        width: 100%;
        height: 140px;
        position: absolute;
        top: 0;
        left: 0;
      `}
    >
      <div
        className={cx(
          'anim__flower',
          css`
            width: 100%;
            height: 100%;
            opacity: 0;
            transform: scale(0.5);
            transform-origin: bottom;
            will-change: transform, opacity;
          `
        )}
      >
        <FlowerHeadPath id={id} />
        <FlowerHead type={head} face={face} colors={flowerColors} />
        <div
          className={css`
            width: 100%;
            height: 100%;
            position: absolute;
            left: -1px;
            bottom: 0;
          `}
        >
          <div
            className={css`
              width: 30%;
              position: absolute;
              bottom: 9px;
              left: calc(50% + 0.5px);
              transform: translateX(-50%);
            `}
          >
            <FlowerStalk position="left" />
          </div>

          <div
            className={cx(
              'anim__flower-leaves',
              css`
                width: 55px;
                position: absolute;
                bottom: 5px;
                left: 50%;
                transform: translateX(-50%) rotate(-5deg);
                will-change: transform;
              `
            )}
          >
            <FlowerLeaves />
          </div>
          <FlowerPot />
        </div>
      </div>
    </div>
  )
}
